<template>
  <div v-if="data?.type === 'tab_forms'">
    <template v-if="currentView === View.Buttons">
      <p>
        Si deseas mayor información, revisa nuestros
        <a href="http://securitec.pe" target="_blank" rel="noopener noreferrer">docs aquí.</a>
      </p>
      <div v-for="item in props.data?.items" :key="item.id">
        <HelpCenterRoundedButton
          :title="item?.title_menu"
          :description="item?.subtitle_menu"
          @click="openHelpCenter(item.information)"
        />
      </div>
    </template>
  </div>
  <div v-else-if="isNestedScope">
    <template v-if="currentView === View.Buttons">
      <template v-if="intro">
        <h3>{{ intro.title }}</h3>
        <p>
          {{ intro.description }}
          <a
            v-if="docsUrl"
            :href="docsUrl"
            class="docs-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ intro.docsLabel }}
            <IconExternalLink active class="docs-link__icon" />
          </a>
        </p>
      </template>
      <div v-for="item in props.data?.items" :key="item.id">
        <HelpCenterButtonMenu
          v-if="item.show_submenu"
          :title="item?.title_menu"
          :description="item?.subtitle_menu"
          @click="openNestedHelpCenter(item)"
        />
        <div v-if="!item?.show_submenu">
          <HelpCenterFlatView :item="item" />
        </div>
      </div>
    </template>
  </div>
  <div v-else>
    <template v-if="currentView === View.Buttons">
      <p v-if="data?.type === 'menu_workspace'" class="workspace-help-desc">
        En el módulo de Workspace, visualiza tickets, interactúa con contactos por canales y gestiona su información en tiempo real. Conoce más sobre
        <a :href="workspaceHelpUrl" target="_blank" rel="noopener noreferrer" class="workspace-link">
          Workspace <ExportOutlined class="link-icon" />
        </a>
      </p>

      <div v-for="item in props.data?.items" :key="item.id">
        <HelpCenterButtonMenu
          v-if="item.show_submenu"
          :title="item?.title_menu"
          :description="item?.subtitle_menu"
          @click="openHelpCenter(item.information)"
        />
        <div v-if="!item?.show_submenu">
          <HelpCenterFlatView :item="item" />
        </div>
      </div>
    </template>
  </div>
  <template v-if="currentView === View.Results">
    <HelpCenterContent title="" :back-label="backLabel" @back="handleBack">
      <ContentInformation
        :item="selectedItem"
        :type="data?.type"
        :context-label="navigationContextLabel"
        @click="handleInternalLinkClick"
      />
      <div v-if="isNestedScope && selectedChildren.length" class="children">
        <div v-for="child in selectedChildren" :key="child.id">
          <HelpCenterButtonMenu
            v-if="child.show_submenu"
            :title="child?.title_menu"
            :description="child?.subtitle_menu"
            @click="openNestedHelpCenter(child)"
          />
          <div v-if="!child?.show_submenu">
            <HelpCenterFlatView :item="child" />
          </div>
        </div>
      </div>
    </HelpCenterContent>
  </template>
</template>

<script setup lang="ts">
import { computed, h, ref, watch, onMounted } from "vue";
import { ExportOutlined } from "@ant-design/icons-vue";
import { useRouter } from "vue-router";
import { Modal } from "ant-design-vue";
import HelpCenterButtonMenu from "@/app/shared/components/molecules/HelpCenterButtonMenu.vue";
import HelpCenterFlatView from "@/app/shared/components/molecules/HelpCenterFlatView.vue";
import HelpCenterRoundedButton from "@/app/shared/components/molecules/HelpCenterRoundedButton.vue";
import HelpCenterContent from "@/app/shared/components/molecules/HelpCenterContent.vue";
import ContentInformation from "@/app/entities/components/helpCenter/content/index.vue";
import {
  HelpCenterItem,
  HelpCenterInformation,
  HelpCenterSubItem,
} from "@/@types/entities/helpcenter";
import IconExternalLink from "@/app/common/components/atoms/IconExternalLink.vue";
import {
  isNestedHelpCenterScope,
  NESTED_HELP_CENTER_INTRO,
} from "@/app/entities/utils/helpCenter";
import { ExclamationCircleOutlined } from "@ant-design/icons-vue";

const props = defineProps<{
  data: HelpCenterItem;
  defaultOpenTitle?: string;
  initialItemOrders?: number[];
}>();

const router = useRouter();

enum View {
  Buttons = "buttons",
  Results = "results",
}

const currentView = ref(View.Buttons);
const selectedItem = ref<HelpCenterInformation>();
const workspaceHelpUrl = `${import.meta.env.VITE_WEB_DOC}/workspace/vista-workspace`;
const selectedChildren = ref<HelpCenterSubItem[]>([]);
const navigationStack = ref<
  Array<{
    title_menu: string;
    information: HelpCenterInformation;
    children: HelpCenterSubItem[];
  }>
>([]);
const isNestedScope = computed(() => isNestedHelpCenterScope(props.data?.type));

const intro = computed(() => NESTED_HELP_CENTER_INTRO[props.data?.type ?? ""]);

const docsUrl = computed(() =>
  intro.value?.docsPath ? `${import.meta.env.VITE_WEB_DOC}${intro.value.docsPath}` : ""
);

const backLabel = computed(() =>
  isNestedScope.value && navigationStack.value.length === 1
    ? "Volver a menú principal"
    : "Volver"
);

const navigationContextLabel = computed(() => {
  if (!isNestedScope.value || navigationStack.value.length < 2) {
    return "";
  }

  return navigationStack.value.at(-2)?.title_menu || "";
});

const openHelpCenter = (info: HelpCenterInformation) => {
  selectedItem.value = info;
  selectedChildren.value = [];
  currentView.value = View.Results;
};

const checkDefaultOpen = () => {
  if (props.defaultOpenTitle && props.data?.items) {
    const matchedItem = props.data.items.find(
      (item) => item.title_menu === props.defaultOpenTitle
    );
    if (matchedItem) {
      openHelpCenter(matchedItem.information);
    }
  }
};

onMounted(() => {
  checkDefaultOpen();
});

watch(
  () => props.data,
  () => {
    currentView.value = View.Buttons;
    checkDefaultOpen();
  }
);

const openNestedHelpCenter = (item: HelpCenterSubItem) => {
  const navigationItem = {
    title_menu: item.title_menu,
    information: item.information,
    children: item.children || [],
  };

  if (currentView.value === View.Buttons) {
    navigationStack.value = [navigationItem];
  } else {
    navigationStack.value.push(navigationItem);
  }

  selectedItem.value = navigationItem.information;
  selectedChildren.value = navigationItem.children;
  currentView.value = View.Results;
};

const resetNavigation = () => {
  currentView.value = View.Buttons;
  selectedItem.value = undefined;
  selectedChildren.value = [];
  navigationStack.value = [];
};

const openInitialNavigation = () => {
  resetNavigation();

  let items = props.data?.items || [];
  for (const order of props.initialItemOrders || []) {
    const item = items.find((candidate) => candidate.order === order);
    if (!item) return;

    openNestedHelpCenter(item);
    items = item.children || [];
  }
};

watch(
  [() => props.data, () => props.initialItemOrders],
  openInitialNavigation,
  { immediate: true, deep: true }
);

const handleBack = () => {
  if (isNestedScope.value && navigationStack.value.length > 1) {
    navigationStack.value.pop();
    const previousItem = navigationStack.value[navigationStack.value.length - 1];

    selectedItem.value = previousItem?.information;
    selectedChildren.value = previousItem?.children || [];
    return;
  }

  currentView.value = View.Buttons;
  selectedItem.value = undefined;
  selectedChildren.value = [];
  navigationStack.value = [];
};

const handleInternalLinkClick = (event: MouseEvent) => {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey
  )
    return;

  const link = (event.target as HTMLElement).closest<HTMLAnchorElement>("a[href]");
  if (!link || link.target === "_blank") return;

  const href = link.getAttribute("href");
  if (!href?.startsWith("/")) return;

  const url = new URL(href, window.location.origin);
  if (url.origin !== window.location.origin) return;

  event.preventDefault();

  const destinationName = link.textContent?.trim() || "módulo seleccionado";
  const destination = `${url.pathname}${url.search}${url.hash}`;

  Modal.confirm({
    title: "Serás dirigido a otro módulo y tu progreso no se guardará, ¿deseas continuar?",
    icon: h(ExclamationCircleOutlined),
    content: `Al salir de la configuración actual, tu progreso no se guardará. Si continúas, serás dirigido a la vista de “${destinationName}”.`,
    okText: "Sí, continuar",
    cancelText: "No, seguir configurando",
    centered: true,
    onOk() {
      router.push(destination);
    },
  });
};
</script>

<style scoped lang="sass">
h3,
p
  color: $gray-13

h3
  color: $gray-9
  font-weight: 600

.workspace-help-desc
  font-size: 14px
  color: #262626
  margin-bottom: 24px
  line-height: 20px
  .workspace-link
    color: #1890FF
    text-decoration: none
    display: inline-flex
    align-items: center
    gap: 4px
    &:hover
      color: #40a9ff
    .link-icon
      font-size: 12px
a
  text-decoration: underline

.docs-link
  display: inline-flex
  align-items: center
  gap: 4px

  &__icon
    width: 12px
    height: 12px
    flex-shrink: 0
</style>
